import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';

export interface PriceRecord4Hour {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataResponse {
  data: PriceRecord4Hour[];
  metadata: {
    requestedFrom: string;
    requestedTo: string;
    recordCount: number;
  };
}

export interface PriceDataWithMetadata {
  data: PriceRecord4Hour[];
  requestedFrom: string;
  requestedTo: string;
  recordCount: number;
}

/**
 * Hook for fetching 4-hour interval price data.
 * Used for 1-month chart views.
 *
 * @param ticker - Stock ticker symbol
 * @param from - Start date (YYYY-MM-DD)
 * @param to - End date (YYYY-MM-DD)
 * @param enabled - Set false to keep the query mounted but never fetch
 */
export const useCompanyPrice4Hour = (
  ticker: string | undefined,
  from: string,
  to: string,
  enabled: boolean = true
) => {
  return useQuery<PriceDataWithMetadata, Error>({
    queryKey: ['companyPrice4Hour', ticker, from, to],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      const params = {
        ticker,
        from,
        to,
      };

      const { data: response } = await api.get<PriceDataResponse>('/company-price-4hour', {
        params,
      });

      // Transform the response to a flatter structure for easier consumption
      return {
        data: response.data,
        requestedFrom: response.metadata.requestedFrom,
        requestedTo: response.metadata.requestedTo,
        recordCount: response.metadata.recordCount,
      };
    },
    enabled: enabled && !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes for 4-hour data
  });
};
